/**
 * CFOP 速拧法课程数据（第一期：两段式入门路线）
 *
 * 与 lessons.js 同构：纯 JS 数据模块，被 lessons.js 合并导出，
 * verify-lessons.mjs 直接校验（setup = formula 的逆，终态必须还原 + 起始态前提断言）。
 *
 * ⚠️ 记法约定：第一期全部公式为纯外层转（U R F D L B），与层先法零新概念。
 *    M 层/宽转/转体版本（更顺手但引擎暂不渲染）留待二期。
 * ⚠️ 所有序列经 cubejs 逐个验证（scratchpad cfop-lab 系列脚本），修改必须重新验证。
 *
 * setup 一律用 inv(公式) 程序求逆（手写逆序列出过错，见层先 L 案例返工记录）。
 */

/** 求序列的逆（倒序 + 每步取反） */
const inv = (f) =>
  f
    .trim()
    .split(/\s+/)
    .reverse()
    .map((t) => (t.endsWith("'") ? t.slice(0, -1) : t.endsWith('2') ? t : t + "'"))
    .join(' ');

export const CFOP_SECTION = {
  id: 'advanced',
  title: '进阶 · CFOP 速拧',
  subtitle: '从 1 分钟到 30 秒的完整路线',
  icon: '⚡',
  lessons: [
    /* ================= 1. 路线总览 ================= */
    {
      id: 'cfop-roadmap',
      title: 'CFOP 总览与学习路线',
      body: `
        <p><b>CFOP</b>（Cross → F2L → OLL → PLL，也叫 Fridrich 方法）是世界主流速拧法，顶尖选手用它做到 5~8 秒。它不是新方法，而是<b>层先法的合并加速版</b>：</p>
        <table class="lesson-table">
          <tr><th>CFOP 阶段</th><th>对应层先法</th><th>提速原理</th></tr>
          <tr><td><b>C</b>ross 十字</td><td>第 1 步</td><td>白面朝下直接做，不翻来翻去，8 步内完成</td></tr>
          <tr><td><b>F</b>2L 前两层</td><td>第 2 + 3 步</td><td>角块和棱块<b>配对后一起入槽</b>，一次做两件事</td></tr>
          <tr><td><b>O</b>LL 顶面朝向</td><td>第 4 + 5 步</td><td>十字和翻角合并处理</td></tr>
          <tr><td><b>P</b>LL 顶层归位</td><td>第 6 + 7 步</td><td>角和棱的位置合并处理</td></tr>
        </table>
        <p><b>本教程走"两段式"入门路线</b>——用最少的公式打通完整 CFOP 流程：</p>
        <ol class="lesson-steps">
          <li><b>直觉 F2L</b>：先学 3 个核心思想 + 10 个常见情形（提速收益最大的一段，从这里开始）</li>
          <li><b>两段式 OLL</b>：先做黄十字（你已经会）、再翻角，共 7 个新公式</li>
          <li><b>两段式 PLL</b>：先归位角（你已经会）、再归位棱，共 4 个新公式</li>
          <li><b>高效十字</b>：最后打磨——盲做、少步、边做边看下一步</li>
        </ol>
        <p class="muted">💡 预期曲线：层先法熟练（约 60~90 秒）→ 学完本章并练两周（30~45 秒）→ 二期全公式 + 手法优化（sub-20）。别急着背公式表，<b>每个新公式先在 3D 演示里单步走三遍</b>再上手。</p>
        <p class="muted">📌 本章全部公式使用你已熟悉的六面记法（U R F D L B），没有新符号。竞速圈常用的中层转（M）、转体（y）等写法与更短的手法版公式，二期引入。</p>
      `,
    },

    /* ================= 2. 高效十字 ================= */
    {
      id: 'cross-advanced',
      title: '高效十字',
      body: `
        <p><b>🎯 目标</b>：白色朝下<b>直接做十字</b>，全程不把白面翻上来看，8 步以内完成。</p>
        <p>层先法教的是"先转到顶层对准、再转 180° 落下"——稳，但绕路。高效十字的三条心法：</p>
        <ol>
          <li><b>白色朝侧的中层棱，一步就能进家</b>：不需要先弄到顶层（下方案例 ①）</li>
          <li><b>规划顺序，别互相破坏</b>：先落哪个后落哪个，让每一步都不拆前面的（案例 ②）</li>
          <li><b>眼睛领先手</b>：转这一步时，眼睛已经在找下一个白棱——十字做完时最好已经看到第一组 F2L</li>
        </ol>
        <p class="muted">💡 练习法：打乱后<b>只做十字</b>，数步数。連续 10 次都在 8 步内、5 秒内，这一课就毕业了。盲做进阶：观察 15 秒记住四个白棱的位置，闭眼完成十字。</p>
      `,
      demo: {
        view: 'bottom',
        cases: [
          {
            id: 'direct',
            label: '① 中层直插',
            brief: '白-绿棱卡在前右中层、白色朝右：一步 F 直接进家，不用绕顶层',
            setup: "F'",
            formula: 'F',
            notes: [
              'F —— 前面顺时针 90°：白-绿棱从中层直接滑进底面，白色恰好朝下 ✓ 心法一：白色朝侧的中层棱最好落',
            ],
          },
          {
            id: 'chain',
            label: '② 连锁双落',
            brief: '两个白棱都在中层：R 落一个、F 再落一个——顺序对了互不干扰',
            setup: "F' R'",
            formula: 'R F',
            notes: [
              'R —— 右面顺时针：白-红棱从后右中层落进底面 ✓ 注意它没有碰到另一个白棱',
              'F —— 前面顺时针：白-绿棱跟着进家 ✓ 十字连锁完成——如果顺序反了，F 会把白-红棱的位置先占掉',
            ],
          },
        ],
      },
    },

    /* ================= 3. F2L 核心思想 ================= */
    {
      id: 'f2l-concept',
      title: 'F2L 核心思想',
      body: `
        <p><b>🎯 F2L 在做什么</b>：把层先法的第 2 步（白角）和第 3 步（中层棱）<b>合并</b>——每次同时处理一个白色角块和它旁边的中层棱块，四组做完，前两层完工。这是 CFOP 提速收益最大的一段。</p>
        <p><b>三个关键词</b>：</p>
        <ul>
          <li><b>槽（Slot）</b>：一个底角位 + 它上面的中层棱位合称一个槽。魔方有 4 个槽，本课程固定用<b>右前槽</b>演示。</li>
          <li><b>配对（Pair）</b>：把属于同一个槽的角块和棱块在顶层拼到一起。</li>
          <li><b>入槽（Insert）</b>：配好对的角棱组合，用 3 步左右一起塞进槽里。</li>
        </ul>
        <p><b>先看三个代表案例</b>（下方 3D 演示，务必单步走一遍）：</p>
        <ul>
          <li><b>① 三步入槽</b>——层先法要十几步的活，F2L 三步搞定，先感受为什么值得学</li>
          <li><b>② 分离 → 配对 → 入槽</b>——完整心路：角和棱分居两处时怎么办</li>
          <li><b>③ 你其实已经会了</b>——层先法第 3 步的公式，本来就是一个标准 F2L 情形</li>
        </ul>
        <p class="muted">⚠️ 学 F2L 的正确姿势：<b>不背公式、看懂思路</b>。每个案例问自己三个问题——角在哪？棱在哪？它们怎么碰头？下一课的 10 个情形都是这三个问题的不同答案。</p>
      `,
      demo: {
        view: 'front',
        cases: [
          {
            id: 'insert3',
            label: '① 三步入槽',
            brief: '角块悬在槽口上方、棱块在后上：R 提槽的瞬间它俩碰头，顺势一起滑入',
            setup: inv("R U R'"),
            formula: "R U R'",
            notes: [
              'R —— 右面提起：空槽被抬上来，同时白角就位等待',
              'U —— 顶层转过来：棱块贴上角块——配对完成的瞬间',
              "R' —— 右面放回：角棱手拉手一起进槽 ✓ 三步 = 层先法第 2+3 步的活",
            ],
          },
          {
            id: 'split7',
            label: '② 分离→配对→入槽',
            brief: '角块白色朝上（最讨厌的朝向）、棱块在右上分离：先拉开距离再会合',
            setup: inv("R U2 R' U' R U R'"),
            formula: "R U2 R' U' R U R'",
            notes: [
              'R —— 提槽：把角块带上来',
              'U2 —— 顶层转 180°：角块和棱块拉开到对角，腾出配对空间',
              "R' —— 放回槽：角块换了个姿势（白色不再朝上）",
              "U' —— 顶层回转：棱块凑过来贴住角块——配对完成",
              'R —— 再提槽',
              'U —— 对齐槽口',
              "R' —— 一起入槽 ✓ 白色朝上的角块都走这个套路：先想办法让白色翻到侧面",
            ],
          },
          {
            id: 'bridge',
            label: '③ 你其实已经会了',
            brief: '角块已经入槽、棱块还在顶层——这就是层先法第 3 步！同一个公式',
            setup: inv("U R U' R' U' F' U F"),
            formula: "U R U' R' U' F' U F",
            notes: [
              'U —— 【前半】棱块转离槽口（层先法第 3 步的原话）',
              'R —— 打开右槽——注意：已经归位的白角被暂时借出来了',
              "U' —— 棱块送到槽口",
              "R' —— 白角带着棱块的位置信息回槽",
              "U' —— 【后半】换个方向",
              "F' —— 打开前槽",
              'U —— 棱块推入',
              'F —— 合上 ✓ 在 CFOP 视角里：这是"角先进槽、棱后补"的标准情形——你早就会 F2L 了',
            ],
          },
        ],
      },
    },

    /* ================= 4. F2L 十大常见情形 ================= */
    {
      id: 'f2l-cases',
      title: 'F2L 十大常见情形',
      body: `
        <p><b>🎯 这一课</b>：直觉 F2L 的实战字典。41 种标准情形里，这 10 个覆盖了绝大多数实战场景——每个都点开单步走一遍，理解"角在哪、棱在哪、怎么碰头"，而不是死背。</p>
        <p><b>判断顺序（实战心法）</b>：</p>
        <ol>
          <li>找一对属于同槽的角和棱（先做已经在顶层的，槽里的留到后面）</li>
          <li>看角块白色朝向：朝侧面（好办）还是朝上（先翻，见情形②⑧）</li>
          <li>看棱块和角块的相对位置：能直接配对？还是要先拉开？</li>
        </ol>
        <p class="muted">💡 每天挑 3 个情形，打乱后专找它做。两周后你会发现自己不再"想公式"，手自己会动——那就是直觉 F2L 成型了。</p>
        <p class="muted">⚠️ 记住老朋友：卡在槽里的错误组合，用 <code>R U R'</code> 掏出来回到顶层再处理（情形⑦⑨就是两种"槽里有错"的典型）。</p>
      `,
      demo: {
        view: 'front',
        layout: 'grid',
        cases: [
          {
            id: 'k1',
            label: '① 基本前插',
            brief: '三步入槽的镜像：角块白色朝前、棱块在左上，从前面进槽',
            setup: inv("F' U' F"),
            formula: "F' U' F",
          },
          {
            id: 'k2',
            label: '② 白朝上 · 棱在前',
            brief: '角块白色朝上、棱块在正前上方：拉开→翻角→配对→入槽',
            setup: inv("R U R' U2 R U R' U' R U R'"),
            formula: "R U R' U2 R U R' U' R U R'",
          },
          {
            id: 'k3',
            label: '③ 角在槽 · 棱从前绕',
            brief: '角块已归位、棱块在右上：从前面绕进去补位（第 3 步公式的镜像走法）',
            setup: inv("U' F' U F U R U' R'"),
            formula: "U' F' U F U R U' R'",
          },
          {
            id: 'k4',
            label: '④ 角在槽但拧着',
            brief: '角块在槽里但白色朝前（拧了）：提出来转个身再回去，棱顺路跟进',
            setup: inv("R U' R' U R U' R'"),
            formula: "R U' R' U R U' R'",
          },
          {
            id: 'k5',
            label: '⑤ 顶层分离 · 白朝右',
            brief: '角棱都在顶层但分居两侧、白色朝右：转顶层让它们相遇',
            setup: inv("U' R U R' U R U R'"),
            formula: "U' R U R' U R U R'",
          },
          {
            id: 'k6',
            label: '⑥ 顶层分离 · 白朝前',
            brief: '⑤ 的镜像：白色朝前，从前面那侧会合入槽',
            setup: inv("U F' U' F U' F' U' F"),
            formula: "U F' U' F U' F' U' F",
          },
          {
            id: 'k7',
            label: '⑦ 同槽错位',
            brief: '角在槽里白色朝右、棱也在槽里但方向反了：整槽重装',
            setup: inv("R U' R' U R U2 R' U R U' R'"),
            formula: "R U' R' U R U2 R' U R U' R'",
          },
          {
            id: 'k8',
            label: '⑧ 三明治',
            brief: '角块白色朝上、棱块已在槽里：反复三次 R U R\' 节奏夹出来再装回',
            setup: inv("R U R' U' R U R' U' R U R'"),
            formula: "R U R' U' R U R' U' R U R'",
          },
          {
            id: 'k9',
            label: '⑨ 同槽全错',
            brief: '角和棱都在槽里但都不对：掏出来按"白朝上"流程重走',
            setup: inv("U R U' R' U' R U R' U2 R U' R'"),
            formula: "U R U' R' U' R U R' U2 R U' R'",
          },
          {
            id: 'k10',
            label: '⑩ 隔槽借位',
            brief: '角棱相邻但方向别扭：借前面的空间转个身直接入槽',
            setup: inv("R U' R' U2 F' U' F"),
            formula: "R U' R' U2 F' U' F",
          },
        ],
      },
    },

    /* ================= 5. 两段式 OLL ================= */
    {
      id: 'oll-2look',
      title: '两段式 OLL：顶面一次全黄',
      body: `
        <p><b>🎯 这一课</b>：F2L 做完后，把顶面全部变黄。分两小段：</p>
        <p><b>第一段 · 黄十字（EO）</b>——就是层先法第 4 步，公式一模一样（<code>F R U R' U' F'</code>），你已经会了。现在的目标是<b>快速识别</b>：抬头一眼判断点/L/一字，直接摆好方向连做，不停顿。</p>
        <p><b>第二段 · 翻角（CO）</b>——层先法只教了 Sune 一个公式、靠重复碰运气；现在把 7 种角朝向情形各配一个专属公式，<b>看一眼、摆好位、一发入魂</b>。识别口诀都写在卡片上：先数"已朝上的黄角"有几个（0/1/2 个），再看歪角的黄贴纸朝哪。</p>
        <table class="lesson-table">
          <tr><th>朝上角数</th><th>情形</th></tr>
          <tr><td>1 个</td><td>Sune / 反 Sune（小鱼，你已认识）</td></tr>
          <tr><td>0 个</td><td>H（黄贴全在左右）/ Pi（一侧立墙）</td></tr>
          <tr><td>2 个相邻</td><td>头灯 / T</td></tr>
          <tr><td>2 个对角</td><td>蝴蝶结</td></tr>
        </table>
        <p class="muted">💡 练习法：自由练习页开「只乱顶层」模式，抬头识别 → 摆位 → 出手，循环 20 分钟。识别比手速重要。</p>
      `,
      demo: {
        view: 'top',
        layout: 'grid',
        diagramMode: 'oll',
        cases: [
          {
            id: 'eo-line',
            label: '一字 → 十字',
            brief: '复习：一字摆横向，做一次（层先第 4 步原公式）',
            setup: inv("F R U R' U' F'"),
            formula: "F R U R' U' F'",
          },
          {
            id: 'eo-l',
            label: 'L → 十字（两连）',
            brief: '复习：L 摆左上角做一次变一字，U2 摆横向再做一次——中间不停手',
            setup: inv("F R U R' U' F' U2 F R U R' U' F'"),
            formula: "F R U R' U' F' U2 F R U R' U' F'",
          },
          {
            id: 'eo-dot',
            label: '点 → 十字（三连）',
            brief: '最坏情况连做三次，节奏：公式 U2 公式 U2 公式',
            setup: inv("F R U R' U' F' U2 F R U R' U' F' U2 F R U R' U' F'"),
            formula: "F R U R' U' F' U2 F R U R' U' F' U2 F R U R' U' F'",
          },
          {
            id: 'co-sune',
            label: 'Sune 小鱼',
            brief: '1 个朝上：把它放左前、鱼头咬向左前角（层先第 5 步老朋友）',
            setup: inv("R U R' U R U2 R'"),
            formula: "R U R' U R U2 R'",
          },
          {
            id: 'co-antisune',
            label: '反 Sune',
            brief: '1 个朝上但鱼游反了：把朝上的角放后右，公式跟 Sune 互为倒放',
            setup: inv("R U2 R' U' R U' R'"),
            formula: "R U2 R' U' R U' R'",
          },
          {
            id: 'co-h',
            label: 'H 双头蛇',
            brief: '0 个朝上、四个歪角的黄贴全在左右两侧：节奏是"半个 Sune + 整个 Sune"',
            setup: inv("R U R' U R U' R' U R U2 R'"),
            formula: "R U R' U R U' R' U R U2 R'",
          },
          {
            id: 'co-pi',
            label: 'Pi 立墙',
            brief: '0 个朝上、左侧两个黄贴纸立成一面墙：墙放左边出手',
            setup: inv("R U2 R2 U' R2 U' R2 U2 R"),
            formula: "R U2 R2 U' R2 U' R2 U2 R",
          },
          {
            id: 'co-headlights',
            label: '头灯',
            brief: '2 个相邻朝上在后排、前面两个黄贴像车灯照着你：头灯朝自己',
            setup: inv("R2 D R' U2 R D' R' U2 R'"),
            formula: "R2 D R' U2 R D' R' U2 R'",
          },
          {
            id: 'co-t',
            label: 'T 字',
            brief: '2 个相邻朝上在前排、歪角的黄贴分朝左右：朝上的双角放前排',
            setup: inv("R U R D R' U' R D' R2"),
            formula: "R U R D R' U' R D' R2",
          },
          {
            id: 'co-bowtie',
            label: '蝴蝶结',
            brief: '2 个朝上但在对角线：把朝上的放右前+左后，8 步对称小连招',
            setup: inv("R' F R B' R' F' R B"),
            formula: "R' F R B' R' F' R B",
          },
        ],
      },
    },

    /* ================= 6. 两段式 PLL ================= */
    {
      id: 'pll-2look',
      title: '两段式 PLL：最后一击',
      body: `
        <p><b>🎯 这一课</b>：顶面全黄后，把顶层块全部换到正确位置——魔方还原。分两小段：</p>
        <p><b>第一段 · 角归位</b>——层先法第 6 步你已经掌握了三轮换（A-perm）和完整流程；新增一个 <b>Y-perm</b> 专治"对角互换"：层先流程要做两遍加中途重找，Y 一遍到位。</p>
        <p><b>第二段 · 棱归位</b>——层先法第 7 步的三轮换（Ua）之外，补齐它的三个兄弟：反向三轮换 Ub、对边互换 H、邻边互换 Z。四个公式覆盖全部棱情形，从此最后一步永远一发收尾。</p>
        <table class="lesson-table">
          <tr><th>识别（转 U 对齐后看侧面）</th><th>用哪个</th></tr>
          <tr><td>一条棱归位，其余三条转圈</td><td>Ua / Ub（看转向）</td></tr>
          <tr><td>没有棱归位，对面两两互换</td><td>H</td></tr>
          <tr><td>没有棱归位，相邻两两互换</td><td>Z（= Ub 两连，不用背新的）</td></tr>
        </table>
        <p class="muted">💡 到此为止你有了一套完整的 2-look CFOP：F2L 直觉 + 3 个 EO + 7 个 CO + 6 个 PLL。稳定 sub-40 后再考虑二期全公式。</p>
      `,
      demo: {
        view: 'top',
        layout: 'grid',
        diagramMode: 'pll',
        cases: [
          {
            id: 'pll-aa',
            label: 'A-perm 三角换',
            brief: '复习：左前角不动、其余三角轮换（层先第 6 步原公式）',
            setup: inv("R' F R' B2 R F' R' B2 R2"),
            formula: "R' F R' B2 R F' R' B2 R2",
          },
          {
            id: 'pll-y',
            label: 'Y-perm 对角换',
            brief: '两角对角互换（层先流程要两遍，Y 一遍到位；顺带动两条棱，反正棱还没做）',
            setup: inv("F R U' R' U' R U R' F' R U R' U' R' F R F'"),
            formula: "F R U' R' U' R U R' F' R U R' U' R' F R F'",
          },
          {
            id: 'pll-ua',
            label: 'Ua 棱三换',
            brief: '复习：后棱不动、其余三棱轮换（层先第 7 步原公式）',
            setup: inv("R U' R U R U R U' R' U' R2"),
            formula: "R U' R U R U R U' R' U' R2",
          },
          {
            id: 'pll-ub',
            label: 'Ub 反向棱三换',
            brief: 'Ua 的反方向兄弟：转向不对时不用做两遍 Ua，直接一发 Ub',
            setup: inv("R2 U R U R' U' R' U' R' U R'"),
            formula: "R2 U R U R' U' R' U' R' U R'",
          },
          {
            id: 'pll-h',
            label: 'H 对边互换',
            brief: '前后互换、左右互换：R2 与 U 的机械节奏，闭着眼都能盘',
            setup: inv("R2 U2 R U2 R2 U2 R2 U2 R U2 R2"),
            formula: "R2 U2 R U2 R2 U2 R2 U2 R U2 R2",
          },
          {
            id: 'pll-z',
            label: 'Z 邻边互换',
            brief: '相邻两两互换：不用背新公式——Ub、转 U、再 Ub、收 U\'',
            setup: inv("R2 U R U R' U' R' U' R' U R' U R2 U R U R' U' R' U' R' U R' U'"),
            formula: "R2 U R U R' U' R' U' R' U R' U R2 U R U R' U' R' U' R' U R' U'",
          },
        ],
      },
    },

    /* ================= 7. 练习方法论 ================= */
    {
      id: 'cfop-practice',
      title: '练习方法论',
      body: `
        <p>公式学完只是拿到地图，速度来自正确的练法。四个阶段循环：</p>
        <ol class="lesson-steps">
          <li><b>分段计时</b>：在自由练习页分别测十字 / F2L / 最后一层的用时。哪段最慢练哪段——大多数人的瓶颈在 F2L。</li>
          <li><b>慢拧（Slow Solving）</b>：故意放慢到 60~90 秒完成一次还原，但<b>全程不许停顿</b>。练的是眼睛：转当前一组时必须已经看到下一组。</li>
          <li><b>专项循环</b>：自由练习页开「只乱顶层」，20 分钟内只练 OLL/PLL 的识别与出手；F2L 则挑当天最不熟的 2~3 个情形反复做。</li>
          <li><b>完整计时</b>：每天收尾做 5 次全还原计时取平均。只和自己昨天比。</li>
        </ol>
        <table class="lesson-table">
          <tr><th>阶段目标</th><th>关键抓手</th></tr>
          <tr><td>60 → 45 秒</td><td>F2L 不停顿（慢拧练眼）</td></tr>
          <tr><td>45 → 35 秒</td><td>OLL/PLL 识别不超过 2 秒</td></tr>
          <tr><td>35 → 30 秒</td><td>十字 8 步内盲做 + 十字接第一组 F2L 无缝</td></tr>
          <tr><td>sub-30 之后</td><td>二期：全 OLL/PLL、手法版公式（M 层/宽转）、F2L 全 41 式</td></tr>
        </table>
        <p style="margin-top:14px"><a href="#play" data-route="play" class="btn">打开自由练习 · 分段计时 →</a></p>
      `,
    },
  ],
};
